<script>
   /**************************************************************/
   /* AcceptancePlot/ImportancePlot for training app             */
   /* --------------------                                       */
   /* shows plot with score and orthogonal distances             */
   /* and acceptance areas  (both for objects and variables)     */
   /**************************************************************/

   import { max } from 'mdatools/stat';
   import { Axes, XAxis, YAxis, Box } from 'svelte-plots-basic/2d';

   import AcceptancePlotSeries from './AcceptancePlotSeries.svelte';
   import AcceptancePlotLegend from './AcceptancePlotLegend.svelte';
   import AcceptancePlotLimits from './AcceptancePlotLimits.svelte';

   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS } from '../../../shared/plots/Defaults.js';

   export let params;                     // JSON with main plot parameters
   export let res;                        // JSON with SIMCA results
   export let ncomp = 1;                  // number of components selected by user
   export let selected = undefined;       // selected object or variable

   export let fileName = null;
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;

   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // constant plot parameters
   const showGrid = true;

   // scope (comes from plot settings) defines which distance to use
   // 'objects' — h/q distances
   // 'variables' — g/r distances
   let scope = 'objects';

   // local copies of other plot parameters
   let transType = 'no';
   let showLabels = 'no';
   let resID = res ? Object.keys(res)[0] : '';
   let isAlt = false;
   let colorBy = 'roles';

   // max values for distance points coordinates
   let psXMax = 1;
   let psYMax = 1;

   // max values for limit lines coordinates
   let lmXMax = 1;
   let lmYMax = 1;

   // function for creating axis titles
   function get_axis_label(id, transType) {
      const distNames = {
         'q': 'Orthogonal distance',
         'h': 'Score distance',
         'f': 'Full distance',
         'g': 'Orthogonal distance',
         'r': 'Score distance',
         'i': 'Importance distance'
      };
      return distNames[id] + ', ' + (transType === 'log' ? 'log(1 + ' + id + '/' + id + '0)' : id + '/' + id + '0');
   };

   function select(e) {
      if (selected === undefined) return; // do not select points if this option is not activated in parent app
      const id = parseInt(e.detail.elementID)
      const series_title = e.detail.seriesTitle
      if (id >= 0 && series_title === 'distance-points') {
         selected = id;
      }
   }

   function resetSelection(e) {
      selected = -1;
   }

   // reactive expressions to monitor changes in plot parameters
   $: if (params && params.resID !== resID) resID = params.resID;
   $: if (params && params.transType !== transType) transType = params.transType;
   $: if (params && params.showLabels !== showLabels) showLabels = params.showLabels;
   $: if (params && params.scope) scope = params.scope;
   $: if (params && params.isAlt) isAlt = params.isAlt;
   $: if (params && params.colorBy) colorBy = params.colorBy;

   // select result object for user selected ID
   $: resobj = res && resID ? res[resID] : null;

   // reactive calculations for axis labels and title
   $: title = (scope === 'objects' ? 'Acceptance' : 'Importance') + ' plot (A = ' + ncomp + ')';
   $: xLabel = get_axis_label(scope === 'objects' ? 'h' : 'g', transType);
   $: yLabel = get_axis_label(scope === 'objects' ? 'q' : 'r', transType);

   // reactive calculations for axis limits
   $: xMax = max([psXMax, lmXMax]);
   $: yMax = max([psYMax, lmYMax]);
   $: limX = [-0.02 * xMax, 1.05 * xMax];
   $: limY = [-0.02 * yMax, 1.20 * yMax];
</script>

{#if resobj && params}
<div class="plot-container">
<Axes
   margins={MARGINS}
   on:markerclick={select}
   on:axesclick={resetSelection}
   downloadLinks="hover"
   fileName={fileName ? fileName : 'plot-' + (scope === 'objects' ? 'acceptance' : 'importance')}
   {xLabel}
   {yLabel}
   {limX}
   {limY}
   {title}
   {pngWidth}
   {pngHeight}
   {pngRes}
>
   <AcceptancePlotLimits
      bind:xMax={lmXMax}
      bind:yMax={lmYMax}
      {isAlt}
      {scope}
      {resobj}
      {transType}
      {ncomp}
   />

   <AcceptancePlotSeries
      bind:xMax={psXMax}
      bind:yMax={psYMax}
      {isAlt}
      {ncomp}
      {resobj}
      {scope}
      {colorBy}
      {transType}
      {showLabels}
      {selected}
   />

   {#if scope === 'objects'}
   <AcceptancePlotLegend
      {isAlt}
      {resobj}
      {colorBy}
      {ncomp}
   />
   {/if}

   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
</div>

{#if selected !== undefined}
   {#if selected < 0}
   <div class="msg-select">Click on a point to select...</div>
   {:else}
   <div class="msg-select">Click anywhere to remove selection...</div>
   {/if}
{/if}
{/if}


<style>
   .plot-container {
      width: 100%;
      height: calc(100% - 15px);
   }
   .msg-select {
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }
</style>

